import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';

import ConstructionIcon from '@mui/icons-material/Construction';
import type { GridRowId } from '@mui/x-data-grid';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { ensureDefined } from '@e-socna/utils';

import { SpeedDial } from '../../../../components/SpeedDial';
import { RightDrawer } from '../../../../components/RightDrawer';
import { useActionBlocker } from '../../../../hooks/useActionBlocker.hook';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MaterialList } from '../../../../components/MaterialList';
import { useIdFactory } from '../../../../hooks/useIdFactory.hook';
import { useFindAllMaterialsQuery } from '../../../../components/MaterialList/hooks/useFindAllMaterialsQuery.hook';
import { renumber } from '../../../../utils/renumber.util';

import { FormVariant } from '../../../enums/variant.enum';
import { MaterialForm } from '../../../Material';
import type { MaterialMutationResult } from '../../../Material/hooks/useSubmit.hook';

import type { MaterialUsageForm_MaterialUsage } from '../../fragments/material-usage.fragment';

interface Props {
  readonly fieldName: string;
  readonly materialUsages: readonly MaterialUsageForm_MaterialUsage[];
}

// Fills the scrolling wizard step so the data grid scrolls its rows instead of
// growing with them; below 300px the step itself scrolls.
const EMBEDDED_LIST_SX = {
  boxSizing: 'border-box',
  height: '100%',
  minHeight: 300,
  pt: 3,
  pb: 1,
};

export const Selection = ({ fieldName, materialUsages }: Readonly<Props>) => {
  const { setFieldValue } = useFormikContext();

  const referencesContext = useReferencesContext();

  const { defaultUnit } = ensureDefined({
    defaultUnit: referencesContext.defaultUnit,
  });

  const { data } = useFindAllMaterialsQuery();
  const allMaterials = data?.findAllMaterials;

  const { generateId } = useIdFactory();

  const handleRowSelectionModelChange = useCallback(
    (selection: Readonly<GridRowSelectionModel>) => {
      const newIds = [...selection.ids] as readonly GridRowId[];
      const currentIdsSet = new Set<GridRowId>(
        materialUsages.map((usage) => usage.materialId),
      );
      const newIdsSet = new Set<GridRowId>(newIds);

      const kept = materialUsages.filter((usage) =>
        newIdsSet.has(usage.materialId),
      );

      const idsToAdd = newIds.filter((id) => !currentIdsSet.has(id));

      const added = idsToAdd.map((id, i) => {
        const { materialUsed } = ensureDefined({
          materialUsed: allMaterials?.find((material) => material._id === id),
        });

        return {
          __typename: 'MaterialUsage' as const,
          _id: generateId(),
          materialId: materialUsed._id,
          quantity: {
            __typename: 'Quantity' as const,
            value: 1,
            unitId: defaultUnit._id,
          },
          index: kept.length + i,
        };
      });

      void setFieldValue(fieldName, renumber([...kept, ...added]));
    },
    [
      allMaterials,
      defaultUnit._id,
      fieldName,
      generateId,
      materialUsages,
      setFieldValue,
    ],
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleSuccess = useCallback(
    (result: MaterialMutationResult) => {
      setIsDrawerOpen(false);

      const { id } = ensureDefined({
        id:
          'createMaterial' in result
            ? result.createMaterial._id
            : 'findOneAndUpdateMaterial' in result
              ? result.findOneAndUpdateMaterial._id
              : undefined,
      });

      const newUsage: MaterialUsageForm_MaterialUsage = {
        __typename: 'MaterialUsage' as const,
        _id: generateId(),
        materialId: id,
        quantity: {
          __typename: 'Quantity' as const,
          unitId: defaultUnit._id,
          value: 1,
        },
        index: materialUsages.length,
      };

      void setFieldValue(fieldName, renumber([...materialUsages, newUsage]));
    },
    [defaultUnit._id, fieldName, generateId, materialUsages, setFieldValue],
  );

  const [blockableCloseDrawer, setMustBlockAction] =
    useActionBlocker(closeDrawer);

  return (
    <>
      <MaterialList
        checkboxSelection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={{
          type: 'include',
          ids: new Set(
            materialUsages.map((materialUsage) => materialUsage.materialId),
          ),
        }}
        sx={EMBEDDED_LIST_SX}
      />
      <SpeedDial
        actions={[
          {
            id: 'createMaterial',
            icon: <ConstructionIcon />,
            name: 'Créer Matériel',
            onClick: openDrawer,
          },
        ]}
        title="Actions possibles sur les Matériels"
      />
      <RightDrawer isOpen={isDrawerOpen} onDrawerClose={blockableCloseDrawer}>
        <MaterialForm
          formVariant={FormVariant.Creation}
          onCancel={blockableCloseDrawer}
          onSuccess={handleSuccess}
          setMustBlockAction={setMustBlockAction}
          showCancelButton
          title="Formulaire de création d'un Matériel"
        />
      </RightDrawer>
    </>
  );
};
